import { computed, ref } from 'vue'
import { extractApiErrorMessage } from '@/admin/utils/adminApiUtils'

const TOAST_LIFETIME_MS = 5000
let toastCounter = 0

const redirectByStatus = status => {
  if (typeof window === 'undefined') return false
  if (status === 401 && window.location.pathname !== '/login') {
    window.location.assign('/login')
    return true
  }
  if (status === 403 && window.location.pathname !== '/no-access') {
    window.location.assign('/no-access')
    return true
  }
  return false
}

export function useAdminRequestState() {
  const pendingRequests = ref(0)
  const errorMessage = ref('')
  const successMessage = ref('')
  const toasts = ref([])

  const isLoading = computed(() => pendingRequests.value > 0)

  const removeToast = id => {
    toasts.value = toasts.value.filter(toast => toast.id !== id)
  }

  const pushToast = (type, message) => {
    const normalizedMessage = String(message || '').trim()
    if (!normalizedMessage) return

    toastCounter += 1
    const id = toastCounter
    toasts.value = [...toasts.value, { id, type, message: normalizedMessage }]

    window.setTimeout(() => {
      removeToast(id)
    }, TOAST_LIFETIME_MS)
  }

  const pushError = message => {
    const normalized = String(message || '').trim()
    errorMessage.value = normalized
    if (normalized) pushToast('danger', normalized)
  }

  const pushSuccess = message => {
    const normalized = String(message || '').trim()
    successMessage.value = normalized
    if (normalized) pushToast('success', normalized)
  }

  const clearMessages = () => {
    errorMessage.value = ''
    successMessage.value = ''
  }

  const execute = async (request, { success, error } = {}) => {
    pendingRequests.value += 1
    try {
      const result = await request()
      if (success) pushSuccess(success)
      return result
    } catch (requestError) {
      const status = Number(requestError?.response?.status || 0)
      if (redirectByStatus(status)) throw requestError
      const fallback = error || 'Не удалось выполнить запрос.'
      pushError(extractApiErrorMessage(requestError, fallback))
      throw requestError
    } finally {
      pendingRequests.value = Math.max(0, pendingRequests.value - 1)
    }
  }

  return {
    isLoading,
    errorMessage,
    successMessage,
    toasts,
    clearMessages,
    pushError,
    pushSuccess,
    execute,
    removeToast,
  }
}
